import React, { useCallback, useMemo } from 'react'
import { IonCol, IonGrid, IonLabel, IonRow } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { AccordianComponent } from '../../../components/accordian/Accordian.components'
import { setPainScales } from '../store/vitals.actions'
import { getVitalOptionsSelector } from '../../options/selectors'
import { IRootState } from '../../../store'
import { IScaleOption, IPainScalesSectionProps, IScaleOptionDefinition } from '../store/vitals.types'
import './PainScalesSection.component.css'
import { scaleCombinedTotals, scaleSectionTotal } from '../utils/vitals.util'

const PainScalesSection: React.FC<IPainScalesSectionProps> = ({ isDisabled = false }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { painScales } = useSelector((state: IRootState) => state.vitals)

  const vitalOptions = useSelector(getVitalOptionsSelector)
  const configuredScales = useMemo(
    () =>
      Array.isArray(vitalOptions.additionalPainScaleOptions)
        ? (vitalOptions.additionalPainScaleOptions as IScaleOptionDefinition[])
        : [],
    [vitalOptions.additionalPainScaleOptions]
  )

  const selections = useMemo(() => painScales?.painScaleSelections ?? [], [painScales?.painScaleSelections])

  const setSelectedOptionForSection = useCallback(
    (scaleId: number, sectionLabel: string, option: IScaleOption | null) => {
      const nextSelections = selections.filter((s) => !(s.scaleId === scaleId && s.sectionLabel === sectionLabel))
      if (option) {
        nextSelections.push({
          scaleId,
          sectionLabel,
          id: option.id,
          name: option.name,
          score: option.score,
          nemsis3: option.nemsis3,
        })
      }

      const { totalsCombined } = scaleCombinedTotals(nextSelections, configuredScales)
      dispatch(setPainScales({ totalsCombined, painScaleSelections: nextSelections }))
    },
    [selections, configuredScales, dispatch]
  )

  const handleSelectedOptions = useMemo(() => {
    const map = new Map<string, IScaleOption>()

    for (const selection of selections) {
      map.set(`${selection.scaleId}__${selection.sectionLabel}`, {
        id: selection.id,
        name: selection.name,
        score: selection.score,
        nemsis3: selection.nemsis3,
      })
    }

    return map
  }, [selections])

  return (
    <IonRow className='ion-padding-start ion-padding-end ion-padding-bottom'>
      <IonCol size='12' className='additional-pain-scales-content'>
        <IonGrid className='ion-no-padding'>
          {configuredScales.length === 0 ? (
            <IonRow className='ion-align-items-center pain-scale-no-data-row'>
              <IonCol>
                <IonLabel color='medium' className='pain-scale-no-data-label'>
                  {t('vitals.painScale.noData')}
                </IonLabel>
              </IonCol>
            </IonRow>
          ) : (
            configuredScales.map((scale) => {
              const scaleT = scaleSectionTotal(scale, selections)
              const hasAnySelection = scaleT !== null
              const displayTotal = hasAnySelection ? scaleT : 'N/A'

              return (
                <AccordianComponent
                  key={scale.scaleId}
                  heading={scale.scaleLabel}
                  isCheckMark={hasAnySelection}
                  content={
                    <>
                      {scale.sections?.map((section, index) => (
                        <React.Fragment key={`${scale.scaleId}_${section.sectionLabel}`}>
                          <IonRow>
                            <IonCol className='picklist'>
                              <SingleSelect
                                disabled={isDisabled}
                                dataTestID={`PainScale_${scale.scaleId}_${section.sectionLabel}`}
                                options={section.options}
                                selectedOption={
                                  handleSelectedOptions.get(`${scale.scaleId}__${section.sectionLabel}`) ?? null
                                }
                                listHeader={section.sectionLabel}
                                label={section.sectionLabel}
                                showSelectedOptionId='score'
                                action={(selectedOption) => {
                                  setSelectedOptionForSection(
                                    scale.scaleId,
                                    section.sectionLabel,
                                    selectedOption as IScaleOption | null
                                  )
                                }}
                              />
                            </IonCol>
                          </IonRow>
                          {index < scale.sections.length - 1 && <hr className='field-divider pain-scale-divider' />}
                        </React.Fragment>
                      ))}

                      <hr className='field-divider pain-scale-divider' />
                      <IonRow className='pain-scale-total-row ion-align-items-center ion-justify-content-between'>
                        <IonCol className='pain-scale-total-label-col'>
                          <IonLabel color='medium' className='pain-scale-total-label'>
                            {t('vitals.painScale.total')}
                          </IonLabel>
                        </IonCol>
                        <IonCol size='auto' className='pain-scale-total-value-col'>
                          <IonLabel className='pain-scale-total-value'>{displayTotal}</IonLabel>
                        </IonCol>
                      </IonRow>
                    </>
                  }
                />
              )
            })
          )}
        </IonGrid>
      </IonCol>
    </IonRow>
  )
}

export default PainScalesSection
